// @ts-nocheck
// ============================================================
// TWIN DASHBOARD — Fase 10
// Página principal do Digital Twin — /twin
// ============================================================
import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { useTwinStore, selectCriticalZones, selectActiveRisks, selectHighPriorityRecs } from '@/store/twinStore'
import { startDigitalTwinEngine } from '@/core/digital-twin/digitalTwinEngine'
import { askTwinCopilot } from '@/core/autonomous-copilot/autonomousCopilot'
import { LiveOperationalTwin } from '@/components/twin/LiveOperationalTwin'
import { OperationalMap } from '@/components/twin/OperationalMap'
import { TwinReplay } from '@/components/twin/TwinReplay'
import { TwinStatusBar, AutonomousAlert, RecommendationCard, SimulationCard } from '@/design-system/twin'
import { SURFACE, TEXT } from '@/design-system/tokens'

type TwinView = 'live' | 'map' | 'replay' | 'copilot' | 'simulation'

export default function TwinDashboard() {
  const { obraAtiva, company } = useAuth()
  const obraId    = obraAtiva?.id ?? ''
  const companyId = company?.id  ?? ''

  // FIX PROB-16: Use granular selectors to avoid full-store rerenders
  const twinState       = useTwinStore(s => s.twinState)
  const overallRiskScore = useTwinStore(s => s.overallRiskScore)
  const correlations    = useTwinStore(s => s.correlations)
  const autonomousActions = useTwinStore(s => s.autonomousActions)
  const simulations     = useTwinStore(s => s.simulations)
  const isLoading       = useTwinStore(s => s.isLoading)
  const lastUpdated     = useTwinStore(s => s.lastUpdated)

  const criticalZones   = useTwinStore(selectCriticalZones)
  const activeRisks     = useTwinStore(selectActiveRisks)
  const topRecs         = useTwinStore(selectHighPriorityRecs)

  const [view, setView]           = useState<TwinView>('live')
  const [copilotQ, setCopilotQ]   = useState('')
  const [copilotA, setCopilotA]   = useState<string | null>(null)
  const [copilotLoading, setLoading] = useState(false)

  // ─── Start engine ────────────────────────────────────────
  useEffect(() => {
    if (!obraId || !companyId) return
    let cleanup: (() => void) | null = null
    startDigitalTwinEngine(obraId, companyId, {
      pollIntervalMs:     60_000,
      snapshotIntervalMs: 300_000,
    }).then(fn => { cleanup = fn })
    return () => { cleanup?.() }
  }, [obraId, companyId])

  // ─── Copilot ─────────────────────────────────────────────
  function handleCopilotAsk() {
    if (!copilotQ.trim() || !twinState) return
    setLoading(true)
    const resp = askTwinCopilot({ question: copilotQ, obraId, twinState })
    setCopilotA(resp.answer)
    setLoading(false)
  }

  const tabs: Array<{ id: TwinView; label: string; icon: string }> = [
    { id: 'live',       label: 'Twin ao vivo',  icon: '🔴' },
    { id: 'map',        label: 'Mapa Operacional', icon: '🗺️' },
    { id: 'copilot',    label: 'Copilot',        icon: '🤖' },
    { id: 'simulation', label: 'Simulação',      icon: '⚗️' },
    { id: 'replay',     label: 'Replay',         icon: '⏮' },
  ]

  const activeAlerts = autonomousActions.slice(0, 3)
  const activeCorr   = correlations.filter(c => c.is_active)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100vh', background: '#0a0f1a' }}>
      {/* ─── Top bar ──────────────────────────────────────── */}
      <div style={{
        padding:      '12px 20px',
        background:   SURFACE.panel,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        display:      'flex',
        alignItems:   'center',
        gap:          16,
        flexWrap:     'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: TEXT.primary }}>
            🏗️ Digital Twin
          </h1>
          <div style={{ fontSize: 11, color: TEXT.muted }}>
            {obraId ? `Obra ${obraId}` : 'Nenhuma obra selecionada'}
            {lastUpdated && ` · Atualizado ${new Date(lastUpdated).toLocaleTimeString('pt-BR')}`}
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {[
            { label: 'Risk Score', value: Math.round(overallRiskScore), warn: overallRiskScore >= 45 },
            { label: 'Zonas críticas', value: criticalZones.length, warn: criticalZones.length > 0 },
            { label: 'Riscos ativos', value: activeRisks.length, warn: activeRisks.length > 2 },
            { label: 'Correlações', value: activeCorr.length, warn: activeCorr.length > 0 },
          ].map(k => (
            <div key={k.label} style={{ textAlign: 'center', minWidth: 64 }}>
              <div style={{
                fontSize:   20,
                fontWeight: 800,
                color:      k.warn ? '#f97316' : TEXT.primary,
              }}>{k.value}</div>
              <div style={{ fontSize: 10, color: TEXT.muted }}>{k.label}</div>
            </div>
          ))}
        </div>

        {isLoading && (
          <motion.div
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
            style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }}
          />
        )}
      </div>

      {/* ─── Main layout ─────────────────────────────────── */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '280px 1fr 280px', gap: 0, overflow: 'hidden' }}>

        {/* Left panel — alerts + recs */}
        <div style={{
          borderRight:  '1px solid rgba(255,255,255,0.06)',
          overflowY:    'auto',
          padding:      16,
          display:      'flex',
          flexDirection:'column',
          gap:          12,
        }}>
          {/* Autonomous alerts */}
          {activeAlerts.length > 0 && (
            <div>
              <SectionLabel>🤖 Ações Autônomas</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {activeAlerts.map(a => (
                  <AutonomousAlert
                    key={a.id}
                    title={a.action_type}
                    message={String(a.trigger_data?.rule_nome ?? '')}
                    severity="warning"
                    time={new Date(a.executed_at).toLocaleTimeString('pt-BR')}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Correlations */}
          {activeCorr.length > 0 && (
            <div>
              <SectionLabel>⚡ Correlações de Risco</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {activeCorr.slice(0, 5).map(c => (
                  <AutonomousAlert
                    key={c.id}
                    title={c.title}
                    message={c.description}
                    severity={c.severity === 'emergency' ? 'emergency' : c.severity === 'critical' ? 'critical' : 'warning'}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {topRecs.length > 0 && (
            <div>
              <SectionLabel>💡 Recomendações</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {topRecs.map(r => <RecommendationCard key={r.id} rec={r} />)}
              </div>
            </div>
          )}

          {activeAlerts.length === 0 && activeCorr.length === 0 && topRecs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 8px', color: TEXT.muted, fontSize: 12 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
              Operação estável<br/>Sem alertas ativos
            </div>
          )}
        </div>

        {/* Center — main view */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* View tabs */}
          <div style={{
            display:       'flex',
            gap:           2,
            padding:       '8px 16px',
            borderBottom:  '1px solid rgba(255,255,255,0.06)',
            background:    SURFACE.panel,
            overflowX:     'auto',
          }}>
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setView(t.id)}
                style={{
                  padding:      '6px 14px',
                  borderRadius: 6,
                  border:       'none',
                  background:   view === t.id ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color:        view === t.id ? '#3b82f6' : TEXT.muted,
                  fontSize:     12,
                  fontWeight:   view === t.id ? 700 : 400,
                  cursor:       'pointer',
                  whiteSpace:   'nowrap',
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* View content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <AnimatePresence mode="wait">
              {view === 'live' && (
                <motion.div key="live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ height: '100%' }}>
                  <LiveOperationalTwin />
                </motion.div>
              )}
              {view === 'map' && (
                <motion.div key="map" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ height: '100%' }}>
                  <OperationalMap />
                </motion.div>
              )}
              {view === 'copilot' && (
                <motion.div key="copilot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <CopilotView
                    question={copilotQ}
                    answer={copilotA}
                    loading={copilotLoading}
                    twinAvailable={!!twinState}
                    onChangeQ={setCopilotQ}
                    onAsk={handleCopilotAsk}
                  />
                </motion.div>
              )}
              {view === 'simulation' && (
                <motion.div key="sim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <SimulationView obraId={obraId} />
                </motion.div>
              )}
              {view === 'replay' && (
                <motion.div key="replay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <TwinReplay obraId={obraId} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right panel — recent simulations */}
        <div style={{
          borderLeft:   '1px solid rgba(255,255,255,0.06)',
          overflowY:    'auto',
          padding:      16,
          display:      'flex',
          flexDirection:'column',
          gap:          12,
        }}>
          <SectionLabel>⚗️ Simulações Recentes</SectionLabel>
          {simulations.length > 0
            ? simulations.slice(0, 5).map(s => <SimulationCard key={s.id} result={s} />)
            : <div style={{ textAlign: 'center', padding: '24px 8px', color: TEXT.muted, fontSize: 12 }}>
                Nenhuma simulação executada
              </div>
          }
        </div>
      </div>
    </div>
  )
}

// ─── Copilot View ─────────────────────────────────────────
function CopilotView({
  question, answer, loading, twinAvailable, onChangeQ, onAsk,
}: {
  question: string; answer: string | null; loading: boolean
  twinAvailable: boolean; onChangeQ: (q: string) => void; onAsk: () => void
}) {
  const QUICK = [
    'Qual é a área mais crítica?',
    'Onde reforçar equipe?',
    'Qual o maior risco?',
    'Como está a concretagem?',
    'Há equipamentos com problema?',
    'Como está a logística?',
    'Me dá um resumo geral',
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: TEXT.primary, marginBottom: 4 }}>
          🤖 Copilot Operacional
        </div>
        <div style={{ fontSize: 12, color: TEXT.muted }}>
          Perguntas contextuais sobre o Digital Twin — on-device, sem API externa
        </div>
      </div>

      {/* Quick questions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {QUICK.map(q => (
          <button
            key={q}
            onClick={() => { onChangeQ(q); }}
            style={{
              background:   'rgba(59,130,246,0.1)',
              border:       '1px solid rgba(59,130,246,0.3)',
              borderRadius: 16,
              color:        '#93c5fd',
              fontSize:     11,
              padding:      '4px 10px',
              cursor:       'pointer',
            }}
          >{q}</button>
        ))}
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={question}
          onChange={e => onChangeQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onAsk()}
          placeholder="Faça uma pergunta sobre a obra…"
          disabled={!twinAvailable}
          style={{
            flex:         1,
            background:   SURFACE.card,
            border:       '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            color:        TEXT.primary,
            fontSize:     13,
            padding:      '10px 14px',
          }}
        />
        <button
          onClick={onAsk}
          disabled={!twinAvailable || !question.trim() || loading}
          style={{
            background:   '#3b82f6',
            border:       'none',
            borderRadius: 8,
            color:        '#fff',
            fontSize:     13,
            fontWeight:   700,
            padding:      '10px 18px',
            cursor:       'pointer',
            opacity:      (!twinAvailable || !question.trim()) ? 0.4 : 1,
          }}
        >
          {loading ? '…' : 'Perguntar'}
        </button>
      </div>

      {!twinAvailable && (
        <div style={{ fontSize: 12, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '8px 12px', borderRadius: 6 }}>
          ⚠️ Twin não carregado — inicie o engine ou selecione uma obra
        </div>
      )}

      {/* Answer */}
      <AnimatePresence>
        {answer && (
          <motion.div
            key="answer"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding:      16,
              background:   'rgba(59,130,246,0.08)',
              border:       '1px solid rgba(59,130,246,0.2)',
              borderRadius: 10,
              fontSize:     13,
              color:        TEXT.primary,
              lineHeight:   1.6,
              whiteSpace:   'pre-line',
            }}
          >
            {answer}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Simulation View ──────────────────────────────────────
function SimulationView({ obraId }: { obraId: string }) {
  const { addSimulation, setActiveScenario } = useTwinStore()
  const [tipo, setTipo]     = useState('concretagem_delay')
  const [params, setParams] = useState<Record<string, string>>({})
  const [running, setRunning] = useState(false)

  const SCENARIOS: Array<{ id: string; label: string; params: Array<{ key: string; label: string; default: string }> }> = [
    { id: 'concretagem_delay', label: 'Atraso Concretagem',
      params: [{ key: 'delay_days', label: 'Dias de atraso', default: '2' }, { key: 'volume_m3', label: 'Volume (m³)', default: '100' }] },
    { id: 'workforce_reduction', label: 'Redução de Efetivo',
      params: [{ key: 'reduction_pct', label: 'Redução (%)', default: '30' }] },
    { id: 'supply_rupture', label: 'Ruptura de Supply',
      params: [{ key: 'material', label: 'Material', default: 'concreto' }, { key: 'days_out', label: 'Dias sem material', default: '3' }] },
    { id: 'weather_extreme', label: 'Clima Extremo',
      params: [{ key: 'tipo', label: 'Tipo', default: 'chuva_forte' }, { key: 'duration_hours', label: 'Duração (h)', default: '6' }] },
    { id: 'equipment_failure', label: 'Falha de Equipamento',
      params: [{ key: 'equipment_tipo', label: 'Equipamento', default: 'crane' }, { key: 'repair_days', label: 'Dias reparo', default: '2' }] },
  ]

  const currentScenario = SCENARIOS.find(s => s.id === tipo)!

  async function runSim() {
    setRunning(true)
    const { runSimulation } = await import('@/core/simulation/operationalSimulationEngine')
    const scenario = {
      id: `scen_${Date.now()}`, obra_id: obraId, tipo: tipo as any,
      nome: currentScenario.label, parameters: Object.fromEntries(
        currentScenario.params.map(p => [p.key, params[p.key] ?? p.default])
      ),
      created_at: new Date().toISOString(), is_template: false,
    }
    const result = runSimulation(scenario, {})
    addSimulation(result)
    setRunning(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: TEXT.primary }}>⚗️ Motor de Simulação</div>

      <div>
        <label style={{ fontSize: 12, color: TEXT.muted, display: 'block', marginBottom: 4 }}>Cenário</label>
        <select
          value={tipo}
          onChange={e => { setTipo(e.target.value); setParams({}) }}
          style={{ width: '100%', background: SURFACE.card, color: TEXT.primary, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
        >
          {SCENARIOS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {currentScenario.params.map(p => (
        <div key={p.key}>
          <label style={{ fontSize: 12, color: TEXT.muted, display: 'block', marginBottom: 4 }}>{p.label}</label>
          <input
            value={params[p.key] ?? p.default}
            onChange={e => setParams(prev => ({ ...prev, [p.key]: e.target.value }))}
            style={{ width: '100%', background: SURFACE.card, color: TEXT.primary, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
      ))}

      <button
        onClick={runSim}
        disabled={running}
        style={{
          background:   '#3b82f6', border: 'none', borderRadius: 8,
          color:        '#fff', fontSize: 13, fontWeight: 700,
          padding:      '10px 20px', cursor: 'pointer',
          opacity:      running ? 0.5 : 1,
        }}
      >
        {running ? '⏳ Simulando…' : '▶ Executar Simulação'}
      </button>
    </div>
  )
}

// ─── Section Label ────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize:      10,
      fontWeight:    700,
      color:         TEXT.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom:  4,
    }}>
      {children}
    </div>
  )
}
